import type { FieldErrors } from '@/lib/validate'

import { cleanLine, cleanText, cleanUrl, isEmail } from '@/lib/validate'

/*
 * The two application forms, validated the same way in the browser and on the server.
 */

export const STAGES = ['Pre-seed or seed', 'Series A', 'Series B or later', 'Established company']
export const TIMELINES = ['As soon as possible', 'Within three months', 'Later this year', 'Just exploring']
export const SCOUT_DISCIPLINES = ['Product design', 'Brand', 'Motion', 'Design engineering', 'Leadership']
export const VISIBILITY = ['Visible', 'Anonymous', 'Not sure yet']

export type CompanyField = 'fullName' | 'email' | 'company' | 'website' | 'roleTitle' | 'stage' | 'timeline' | 'notes'

export interface CompanyApplication {
    fullName: string
    email: string
    company: string
    website: string
    roleTitle: string
    stage: string
    timeline: string
    notes: string
}

export const validateCompanyApplication = (input: Record<string, unknown>): { data: CompanyApplication, errors: FieldErrors<CompanyField> } => {
    const rawWebsite = cleanLine(input.website, 300)

    const data: CompanyApplication = {
        fullName: cleanLine(input.fullName, 120),
        email: cleanLine(input.email, 254).toLowerCase(),
        company: cleanLine(input.company, 160),
        website: cleanUrl(rawWebsite),
        roleTitle: cleanLine(input.roleTitle, 160),
        stage: STAGES.includes(cleanLine(input.stage)) ? cleanLine(input.stage) : '',
        timeline: TIMELINES.includes(cleanLine(input.timeline)) ? cleanLine(input.timeline) : '',
        notes: cleanText(input.notes, 3000)
    }

    const errors: FieldErrors<CompanyField> = {}

    if (data.fullName.length < 2) errors.fullName = 'Please tell us your name.'
    if (!isEmail(data.email)) errors.email = 'Please enter a valid email address.'
    if (data.company.length < 2) errors.company = 'Please tell us the name of your company.'
    if (rawWebsite && !data.website) errors.website = 'That does not look like a web address.'
    if (data.roleTitle.length < 2) errors.roleTitle = 'Which role are you hiring for?'
    if (!data.stage) errors.stage = 'Please choose the stage that fits best.'
    if (!data.timeline) errors.timeline = 'Please choose a rough timeline.'

    return { data, errors }
}

export type ScoutField = 'fullName' | 'email' | 'currentRole' | 'portfolioUrl' | 'disciplines' | 'recommendation' | 'visibility'

export interface ScoutApplication {
    fullName: string
    email: string
    currentRole: string
    portfolioUrl: string
    disciplines: string[]
    recommendation: string
    visibility: string
}

export const validateScoutApplication = (input: Record<string, unknown>): { data: ScoutApplication, errors: FieldErrors<ScoutField> } => {
    const rawPortfolio = cleanLine(input.portfolioUrl, 300)
    const disciplines = Array.isArray(input.disciplines)
        ? input.disciplines.map(item => cleanLine(item)).filter(item => SCOUT_DISCIPLINES.includes(item))
        : []

    const data: ScoutApplication = {
        fullName: cleanLine(input.fullName, 120),
        email: cleanLine(input.email, 254).toLowerCase(),
        currentRole: cleanLine(input.currentRole, 200),
        portfolioUrl: cleanUrl(rawPortfolio),
        disciplines: Array.from(new Set(disciplines)),
        recommendation: cleanText(input.recommendation, 3000),
        visibility: VISIBILITY.includes(cleanLine(input.visibility)) ? cleanLine(input.visibility) : ''
    }

    const errors: FieldErrors<ScoutField> = {}

    if (data.fullName.length < 2) errors.fullName = 'Please tell us your name.'
    if (!isEmail(data.email)) errors.email = 'Please enter a valid email address.'
    if (data.currentRole.length < 2) errors.currentRole = 'Tell us what you do and where.'
    if (!rawPortfolio) errors.portfolioUrl = 'Add a link to your work, a portfolio or a profile.'
    else if (!data.portfolioUrl) errors.portfolioUrl = 'That does not look like a web address.'
    if (data.disciplines.length === 0) errors.disciplines = 'Choose at least one area you know well.'
    if (data.recommendation.length < 20) errors.recommendation = 'A sentence or two is plenty, but we would love to hear it.'
    if (!data.visibility) errors.visibility = 'Please choose one. You can change it later.'

    return { data, errors }
}
